import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import {
  associateTemplateCategory,
  fetchCategoryDetails,
} from '~/redux/actions/admin-templates-actions';
import { getAllTemplates } from '~/redux/actions/template-builder';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE } from '~/utils/constants';
import { focusAdjacentField } from '~/utils/focus-navigation';
import {
  getDropdownMenuWidth,
  getDropdownMenuWidthStyles,
  getDropdownSearchPlaceholder,
} from '~/utils/dropdown-menu';

// Components
import Loader from '~/components/General/Loader';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';
import OlcDropdownChevron from '~/components/General/OlcDropdownChevron';

// MUI Components
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  Input,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';

// Assets
import { CheckedCIcon, MultiSelectCrossCIcon, UncheckedCIcon } from '~/assets/images';

// Styles
import './templateDropdown.scss';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const TEMPLATE_SEARCH_DEBOUNCE_MS = 300;

const menuBaseProps = {
  PaperProps: {
    style: {
      maxWidth: 'calc(100vw - 32px)',
      overflow: 'hidden',
    },
  },
};

const CategoryTemplateDropdown = (props) => {
  const {
    options = [],
    selectedValue = [],
    setSelectedValue,
    placeHolderText = '',
    searchFieldPlaceholderText = '',
    noOptionText = 'No templates found',
    onSearchChange = () => {},
    searchLoading = false,
    disabled = false,
  } = props;

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuWidth, setMenuWidth] = useState(null);
  const [search, setSearch] = useState('');
  const fieldRootRef = useRef(null);
  const menuListRef = useRef(null);
  const searchInputRef = useRef(null);
  const scrollTopRef = useRef(0);

  const selectedIds = useMemo(
    () => new Set((selectedValue || []).map((value) => String(value))),
    [selectedValue]
  );

  const searchPlaceholder = getDropdownSearchPlaceholder(
    searchFieldPlaceholderText,
    placeHolderText
  );

  const handleMenuOpen = () => {
    setMenuWidth(getDropdownMenuWidth(fieldRootRef.current));
    setMenuOpen(true);
  };

  const handleMenuClose = useCallback(() => {
    setMenuOpen(false);
    setSearch('');
    onSearchChange('');
  }, [onSearchChange]);

  const handleSearchChange = useCallback(
    (event) => {
      const nextSearch = event.target.value;
      setSearch(nextSearch);
      onSearchChange(nextSearch);
    },
    [onSearchChange]
  );

  const handleSearchClear = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();
      setSearch('');
      onSearchChange('');
      setTimeout(() => searchInputRef.current?.focus(), 0);
    },
    [onSearchChange]
  );

  const handleOptionToggle = useCallback(
    (optionId) => {
      if (menuListRef.current) {
        scrollTopRef.current = menuListRef.current.scrollTop;
      }

      const selectedId = String(optionId);
      const currentSelectedValue = selectedValue || [];
      const nextSelectedValue = selectedIds.has(selectedId)
        ? currentSelectedValue.filter((value) => String(value) !== selectedId)
        : [...currentSelectedValue, optionId];

      setSelectedValue(nextSelectedValue);
    },
    [selectedIds, selectedValue, setSelectedValue]
  );

  useLayoutEffect(() => {
    if (menuOpen && menuListRef.current) {
      menuListRef.current.scrollTop = scrollTopRef.current;
    }
  }, [menuOpen, options]);

  useEffect(() => {
    if (!menuOpen) return;

    setTimeout(() => searchInputRef.current?.focus(), 0);
  }, [menuOpen, search, selectedValue]);

  const effectiveSearchLoading = Boolean(searchLoading && menuOpen);

  return (
    <FormControl ref={fieldRootRef} className="categoryTemplateAssociationDropdown">
      <Select
        multiple
        displayEmpty
        open={menuOpen}
        value={selectedValue}
        disabled={disabled}
        onOpen={handleMenuOpen}
        onClose={handleMenuClose}
        onChange={(event) => {
          const value = event.target.value;
          setSelectedValue(typeof value === 'string' ? value.split(',') : value);
        }}
        input={<OutlinedInput />}
        inputProps={{
          'aria-label': placeHolderText || 'Select templates',
        }}
        renderValue={() => (
          <Box className="categoryTemplateSelectValue">
            <Typography component="span" className="categoryTemplateSelectLabel">
              {placeHolderText}
            </Typography>
            {selectedValue?.length > 0 ? (
              <Typography
                component="span"
                className="categoryTemplateSelectCount"
                aria-label={`${selectedValue.length} selected`}
              >
                {selectedValue.length}
              </Typography>
            ) : null}
          </Box>
        )}
        MenuProps={{
          ...menuBaseProps,
          disableScrollLock: true,
          disableAutoFocusItem: true,
          disableAutoFocus: true,
          disableEnforceFocus: true,
          disableRestoreFocus: true,
          autoFocus: false,
          PaperProps: {
            ...menuBaseProps.PaperProps,
            style: {
              ...menuBaseProps.PaperProps.style,
              ...getDropdownMenuWidthStyles(menuWidth),
            },
          },
          classes: {
            paper: 'categoryTemplateAssociationMenu olcSearchableMenu',
          },
          MenuListProps: {
            ref: menuListRef,
            onMouseDown: (event) => event.stopPropagation(),
            style: {
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
              overflowY: 'auto',
            },
          },
        }}
        IconComponent={(iconProps) => (
          <OlcDropdownChevron
            {...iconProps}
            open={menuOpen}
            style={{
              ...iconProps.style,
              marginTop: '-2.5px',
              right: '20px',
              top: '50%',
            }}
          />
        )}
        className="categoryTemplateSelect"
      >
        <Box
          key="templateSearchFieldInput"
          className="categoryTemplateSearchInputWrapper"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          {effectiveSearchLoading ? (
            <LinearProgress
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                borderTopLeftRadius: '6px',
                borderTopRightRadius: '6px',
                pointerEvents: 'none',
                backgroundColor: '#ED5C2F1F',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#ed5c2f',
                },
              }}
            />
          ) : null}
          <Input
            placeholder={searchPlaceholder}
            fullWidth
            autoFocus
            inputRef={searchInputRef}
            onChange={handleSearchChange}
            value={search}
            autoComplete="off"
            onKeyDown={(event) => {
              if (event.key === 'Tab') {
                event.preventDefault();
                event.stopPropagation();
                handleMenuClose();
                focusAdjacentField(fieldRootRef.current, event.shiftKey);
                return;
              }
              event.stopPropagation();
            }}
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
            className="categoryTemplateSearchInput"
          />
          {search.length > 0 ? (
            <Box
              component="button"
              type="button"
              className="categoryTemplateSearchClear"
              aria-label="Clear template search"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={handleSearchClear}
            >
              <img src={MultiSelectCrossCIcon} alt="" />
            </Box>
          ) : null}
        </Box>

        {options.length > 0 ? (
          options.map((option) => {
            const optionId = option.id;
            const isChecked = selectedIds.has(String(optionId));

            return (
              <MenuItem
                key={`template-${optionId}`}
                disableRipple
                value={optionId}
                selected={false}
                className={isChecked ? 'categoryTemplateOption selected' : 'categoryTemplateOption'}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  handleOptionToggle(optionId);
                }}
              >
                <Checkbox
                  checked={isChecked}
                  icon={<img src={UncheckedCIcon} alt="" />}
                  checkedIcon={<img src={CheckedCIcon} alt="" />}
                  className="categoryTemplateCheckbox"
                />
                <ListItemText primary={option.title} className="categoryTemplateOptionText" />
              </MenuItem>
            );
          })
        ) : (
          <MenuItem disabled className="categoryTemplateNoOptions">
            <Typography>{noOptionText}</Typography>
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
};

const getTemplateOption = (template) => {
  if (template?.id == null) return null;

  return {
    id: template.id,
    title: `ID:${template.id}${template.title ? ` ${template.title}` : ''}`,
  };
};

const mergeTemplateOptions = (currentOptions, templates) => {
  const optionsById = new Map();

  currentOptions.forEach((option) => {
    optionsById.set(String(option.id), option);
  });

  templates.forEach((template) => {
    const option = getTemplateOption(template);

    if (option) {
      optionsById.set(String(option.id), option);
    }
  });

  return Array.from(optionsById.values());
};

const AssociateTemplatesModal = (props) => {
  const { openModal, handleCloseModal, selectedTemplateCategory, pagination } = props;
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedTemplatesData, setSelectedTemplatesData] = useState([]);
  const [templateOptionCache, setTemplateOptionCache] = useState([]);
  const [isTemplateSearchActive, setIsTemplateSearchActive] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detailLoader, setDetailLoader] = useState(false);
  const openModalRef = useRef(openModal);
  const templateSearchTimeoutRef = useRef(null);
  const templateSearchValueRef = useRef('');
  const templateSearchRequestRef = useRef(0);

  const dispatch = useDispatch();

  const templates = useSelector((state) => state.templateReducer.templates.rows) || [];

  const currentTemplateOptions = useMemo(() => {
    return templates.map(getTemplateOption).filter(Boolean);
  }, [templates]);

  const templateOptions = useMemo(() => {
    const selectedTemplateIds = new Set(selectedTemplates.map((templateId) => String(templateId)));
    const labelOptionsById = new Map();

    [...templateOptionCache, ...selectedTemplatesData, ...currentTemplateOptions].forEach(
      (option) => {
        labelOptionsById.set(String(option.id), option);
      }
    );

    if (isTemplateSearchActive) {
      const searchOptionsById = new Map();

      currentTemplateOptions.forEach((option) => {
        searchOptionsById.set(String(option.id), option);
      });

      return Array.from(searchOptionsById.values());
    }

    const selectedOptions = selectedTemplates.map((templateId) => {
      return (
        labelOptionsById.get(String(templateId)) || {
          id: templateId,
          title: `ID:${templateId}`,
        }
      );
    });

    const orderedOptionsById = new Map();

    selectedOptions.forEach((option) => {
      orderedOptionsById.set(String(option.id), option);
    });

    templateOptionCache.forEach((option) => {
      if (!selectedTemplateIds.has(String(option.id))) {
        orderedOptionsById.set(String(option.id), option);
      }
    });

    return Array.from(orderedOptionsById.values());
  }, [
    currentTemplateOptions,
    isTemplateSearchActive,
    selectedTemplates,
    selectedTemplatesData,
    templateOptionCache,
  ]);

  const updateTemplateSearchMode = useCallback((value = '') => {
    const hasSearchValue = Boolean(value?.trim());
    setIsTemplateSearchActive((previousValue) => {
      return previousValue === hasSearchValue ? previousValue : hasSearchValue;
    });
  }, []);

  const clearTemplateSearchTimeout = useCallback(() => {
    if (templateSearchTimeoutRef.current) {
      clearTimeout(templateSearchTimeoutRef.current);
      templateSearchTimeoutRef.current = null;
    }
  }, []);

  const resetTemplateSearchState = useCallback(() => {
    clearTemplateSearchTimeout();
    templateSearchValueRef.current = '';
    templateSearchRequestRef.current += 1;
    setIsTemplateSearchActive(false);
    setSearchLoading(false);
  }, [clearTemplateSearchTimeout]);

  const onClose = () => {
    handleCloseModal();
    resetTemplateSearchState();
    setSelectedTemplates([]);
    setSelectedTemplatesData([]);
  };

  const handleTemplateChange = useCallback(
    (ids) => {
      setSelectedTemplates(ids || []);
      setSelectedTemplatesData((previousTemplatesData) => {
        const previousTemplatesById = new Map(
          previousTemplatesData.map((template) => [String(template.id), template])
        );

        return (ids || []).map((id) => {
          const previousTemplate = previousTemplatesById.get(String(id));
          if (previousTemplate) return previousTemplate;

          const option = templateOptions.find((template) => String(template.id) === String(id));
          return option ? { id: option.id, title: option.title } : { id, title: `ID:${id}` };
        });
      });
    },
    [templateOptions]
  );

  const handleAssociateTempsWithCategory = async (event) => {
    try {
      event.preventDefault();
      setLoading(true);
      const payload = {
        categoryId: selectedTemplateCategory?.id,
        templateIds: selectedTemplates,
      };
      const response = await dispatch(associateTemplateCategory(payload, pagination));
      if (response.status === 200) {
        dispatch(
          success(
            selectedTemplates?.length <= 0
              ? MESSAGES.ADMIN_DASHBOARD.CATEGORIES.ASSIGN_TEMPLATE.DISASSOCIATE_MESSAGE
              : selectedTemplates?.length > 1
                ? MESSAGES.ADMIN_DASHBOARD.CATEGORIES.ASSIGN_TEMPLATE.MULTI_SUCCESS_MESSAGE
                : MESSAGES.ADMIN_DASHBOARD.CATEGORIES.ASSIGN_TEMPLATE.SUCCESS_MESSAGE
          )
        );
      } else {
        dispatch(success(response?.data?.message));
      }
      onClose();
    } catch (error) {
      dispatch(failure(error?.response?.data?.message));
      return error;
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    }
  };

  const fetchTemplatesBySearch = useCallback(
    async (value = '', requestId) => {
      if (!openModalRef.current || templateSearchRequestRef.current !== requestId) return;

      const normalizedSearch = value?.includes('ID:') ? value?.replace(/ID:\d+\s*/, '') : value;

      try {
        await dispatch(
          getAllTemplates(
            DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.page,
            DEFAULT_PAGINATION_WITHOUT_PAGE_SIZE.pageSize,
            normalizedSearch?.trim() || ''
          )
        );
      } finally {
        if (openModalRef.current && templateSearchRequestRef.current === requestId) {
          setSearchLoading(false);
        }
      }
    },
    [dispatch]
  );

  const handleTemplateSearch = useCallback(
    (value = '') => {
      if (!openModalRef.current) return;

      const nextSearchValue = value || '';

      if (templateSearchValueRef.current === nextSearchValue) return;

      templateSearchValueRef.current = nextSearchValue;
      const requestId = templateSearchRequestRef.current + 1;
      templateSearchRequestRef.current = requestId;

      updateTemplateSearchMode(nextSearchValue);
      setSearchLoading(true);
      clearTemplateSearchTimeout();

      templateSearchTimeoutRef.current = setTimeout(() => {
        templateSearchTimeoutRef.current = null;
        fetchTemplatesBySearch(nextSearchValue, requestId);
      }, TEMPLATE_SEARCH_DEBOUNCE_MS);
    },
    [clearTemplateSearchTimeout, fetchTemplatesBySearch, updateTemplateSearchMode]
  );

  const getSelectedCategoryDetail = useCallback(async () => {
    try {
      setDetailLoader(true);
      const categoryDetail = await dispatch(
        fetchCategoryDetails(selectedTemplateCategory?.id, pagination, { refreshList: false })
      );
      if (categoryDetail.status === 200) {
        const categoryTemplates = categoryDetail.data.data.templates || [];
        const existingTemplates = categoryTemplates.map((template) => {
          return template.id;
        });

        setTemplateOptionCache((previousOptions) =>
          mergeTemplateOptions(previousOptions, categoryTemplates)
        );
        setSelectedTemplatesData(categoryTemplates.map(getTemplateOption).filter(Boolean));
        setSelectedTemplates(existingTemplates);
      }
    } catch (error) {
      return error;
    } finally {
      setDetailLoader(false);
    }
  }, [dispatch, pagination, selectedTemplateCategory?.id]);

  useEffect(() => {
    setTemplateOptionCache((previousOptions) => mergeTemplateOptions(previousOptions, templates));
  }, [templates]);

  useEffect(() => {
    if (openModal) {
      getSelectedCategoryDetail();
    }
  }, [getSelectedCategoryDetail, openModal]);

  useEffect(() => {
    openModalRef.current = openModal;

    if (!openModal) {
      resetTemplateSearchState();
    }
  }, [openModal, resetTemplateSearchState]);

  useEffect(() => {
    return () => {
      clearTemplateSearchTimeout();
      templateSearchRequestRef.current += 1;
    };
  }, [clearTemplateSearchTimeout]);

  return (
    <OlcFormModal
      open={openModal}
      onClose={onClose}
      title={MESSAGES.ADMIN_DASHBOARD.CATEGORIES.ASSIGN_TEMPLATE.TITLE}
      className="olcAdminFormModal"
      footer={
        <>
          <OlcButton variant="secondary" type="button" onClick={onClose}>
            {MESSAGES.ADMIN_DASHBOARD.CATEGORIES.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton
            variant="primary"
            type="submit"
            form="associate-template-category-form"
            disabled={loading || detailLoader}
          >
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '25px !important',
                  height: '25px !important',
                }}
              />
            ) : (
              MESSAGES.ADMIN_DASHBOARD.CATEGORIES.ASSIGN_TEMPLATE.SUBMIT_BUTTON
            )}
          </OlcButton>
        </>
      }
    >
      {detailLoader ? (
        <Loader />
      ) : (
        <form
          id="associate-template-category-form"
          className="olcAdminFormFields"
          onSubmit={handleAssociateTempsWithCategory}
        >
          <Box className="olcAdminField">
            <InputLabel className="inputLabel">
              {MESSAGES.ADMIN_DASHBOARD.CATEGORIES.ASSIGN_TEMPLATE.TEMPLATES_LABEL}
            </InputLabel>
            <CategoryTemplateDropdown
              options={templateOptions}
              selectedValue={selectedTemplates}
              setSelectedValue={handleTemplateChange}
              placeHolderText={
                MESSAGES.ADMIN_DASHBOARD.CATEGORIES.ASSIGN_TEMPLATE.TEMPLATES_PLACEHOLDER
              }
              searchFieldPlaceholderText="templates"
              onSearchChange={handleTemplateSearch}
              searchLoading={searchLoading}
            />
          </Box>
        </form>
      )}
    </OlcFormModal>
  );
};

export default AssociateTemplatesModal;
